import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

/**
 * Diálogo de confirmação padrão do app — substitui o `window.confirm()` nativo
 * por um {@link Modal} consistente com o resto da UI.
 *
 * Uso (promise-based, igual ao `confirm()` nativo):
 *
 * ```tsx
 * const confirm = useConfirm();
 * const ok = await confirm({
 *   title: 'Remover anúncio?',
 *   description: 'Tem certeza que deseja remover este anúncio?',
 *   confirmLabel: 'Remover',
 *   tone: 'danger',
 * });
 * if (!ok) return;
 * ```
 *
 * Resolve `true` no botão de confirmação e `false` ao cancelar / fechar / Esc.
 * O `<ConfirmProvider>` precisa estar montado uma única vez na árvore (App.tsx).
 */

export type ConfirmOptions = {
  title: ReactNode;
  description?: ReactNode;
  /** Texto do botão de confirmação. Default "Confirmar". */
  confirmLabel?: string;
  /** Texto do botão de cancelar. Default "Cancelar". */
  cancelLabel?: string;
  /** Estilo do botão de confirmação. Use `danger` para ações destrutivas. Default `primary`. */
  tone?: 'primary' | 'danger';
};

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm precisa estar dentro de <ConfirmProvider>');
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      // Se já houver um diálogo aberto, resolve o anterior como cancelado.
      resolverRef.current?.(false);
      resolverRef.current = resolve;
      setOpts(options);
      setOpen(true);
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOpen(false);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal
        open={open}
        onClose={() => settle(false)}
        title={opts?.title ?? ''}
        description={opts?.description}
        size="sm"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => settle(false)}>
              {opts?.cancelLabel ?? 'Cancelar'}
            </Button>
            <Button
              type="button"
              variant={opts?.tone === 'danger' ? 'danger' : 'primary'}
              onClick={() => settle(true)}
            >
              {opts?.confirmLabel ?? 'Confirmar'}
            </Button>
          </>
        }
      />
    </ConfirmContext.Provider>
  );
}
